import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlanGrid, IntervalSwitch } from "@/components/billing/PlanGrid";
import { BuildPlan } from "@/components/billing/BuildPlan";
import { CurrentPlan, openPortal } from "@/components/billing/CurrentPlan";
import { ChangePlanDialog, TIER } from "@/components/billing/ChangePlanDialog";


export default function PricingPage() {
  const { reload } = useAuth();
  const qc = useQueryClient();
  const { data: b } = useQuery({ queryKey: ["billing"], queryFn: () => api.get("/billing/me").then((r) => r.data) });
  const [interval, setPeriod] = useState("year");
  const [busy, setBusy] = useState("");
  const [change, setChange] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["billing"] }); reload(); };
  const subscribed = b?.base_source === "subscription";
  const sub = b?.subscription;

  const buy = async (tier, extra = {}) => {
    setBusy(tier);
    try {
      if (interval === "once") {
        const { data } = await api.post("/billing/lifetime", { tier, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      if (subscribed) { setChange({ tier, interval, ...extra }); setBusy(""); return; }
      const { data } = await api.post("/billing/checkout", { tier, interval, ...extra, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const action = (t) => {
    if (t === "free") return subscribed && !sub?.cancel_at_period_end && interval !== "once"
      ? <Button className="w-full" variant="outline" onClick={() => setChange({ tier: "free" })} data-testid="plan-free-action">Switch to Free</Button>
      : <div className="text-center text-xs text-[#A8A090]">Always free for hobby writing</div>;
    const life = b?.lifetime;
    if (life && (life === "commercial" || life === t)) return <Button className="w-full" variant="outline" disabled data-testid={`plan-${t}-action`}>Owned for life</Button>;
    if (interval === "once") return (
      <Button className="w-full" variant={t === "pro" ? "default" : "outline"} onClick={() => buy(t)} disabled={!!busy || !b} data-testid={`lifetime-${t}-button`}>
        {busy === t && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Buy {TIER[t]} once{subscribed ? " (ends your subscription)" : ""}
      </Button>
    );
    const isCurrent = subscribed ? !sub?.features && sub?.tier === t && sub?.interval === interval
      : ["admin", "granted"].includes(b?.base_source) && (b?.base_tier === "commercial" || b?.base_tier === t);
    if (isCurrent) return <Button className="w-full" variant="outline" disabled data-testid={`plan-${t}-action`}>Current plan</Button>;
    const label = subscribed ? `Switch to ${TIER[t]}` : ["trial", "event"].includes(b?.source) && t === "pro" ? "Subscribe to keep Pro" : `Get ${TIER[t]}`;
    return (
      <Button className="w-full" variant={t === "pro" ? "default" : "outline"} onClick={() => buy(t)} disabled={!!busy || !b} data-testid={t === "pro" ? "subscribe-button" : `subscribe-${t}-button`}>
        {busy === t && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{label}
      </Button>
    );
  };
  const current = b ? (b.base_tier === "free" ? "free" : b.base_tier) : null;

  return (
    <div className="min-h-screen lf-glow lf-grain">
      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <Link to="/" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="pricing-back-link"><ChevronLeft className="h-3.5 w-3.5" />All universes</Link>
        <div className="lf-label mb-3 mt-12">Plans</div>
        <h1 className="font-display text-4xl tracking-tight text-[#F3EBDD] sm:text-5xl lg:text-6xl">Forge the whole world.</h1>
        <p className="mt-3 max-w-xl text-base text-[#A8A090]">Writing, the Lore Bible and exports are free forever for hobby writers. Pro adds the tools that bring a world to life. Commercial is Pro, licensed for work you sell. <Link to="/license" className="text-[#F59E0B] hover:underline" data-testid="pricing-license-link">Read the license</Link>.</p>
        <div className="mt-12">
          {(b?.pro || b?.base_source) && <CurrentPlan b={b} onChanged={refresh} />}
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <IntervalSwitch value={interval} onChange={setPeriod} prices={b?.prices} />
            {!b?.pro && b?.has_billing && <button onClick={() => openPortal().catch((e) => toast.error(errMsg(e)))} className="text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="past-invoices-link">View past invoices &amp; billing details</button>}
          </div>
          <PlanGrid prices={b?.prices} interval={interval} action={action} current={sub?.features && subscribed ? "custom" : current} />
          {b && b.base_source !== "admin" && interval !== "once" && !b.lifetime && (
            <BuildPlan key={`${interval}-${(sub?.features || []).join()}`} prices={b.prices} interval={interval} initial={subscribed ? sub?.features || [] : []}
              initialCommercial={subscribed && !!sub?.features && sub.tier === "commercial"}
              action={({ features, commercial, disabled }) => (
                <Button onClick={() => buy("custom", { features, commercial })} disabled={disabled || !!busy} data-testid="build-subscribe-button">
                  {busy === "custom" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{subscribed ? "Switch to this plan" : "Get my custom plan"}
                </Button>
              )} />
          )}
        </div>
      </div>
      {change && <ChangePlanDialog target={change} sub={sub} onClose={() => setChange(null)} onDone={refresh} />}
    </div>
  );
}
